import React, { useEffect } from 'react';
import { Bug, Monitor, Rss, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useLabsSettings } from '../hooks/useLabsSettings';

function ToggleRow({
  name,
  label,
  description,
  checked,
  onChange,
}: {
  name: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <label htmlFor={name} className="flex-1 cursor-pointer">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        {description && <p className="text-xs text-slate-500">{description}</p>}
      </label>
      <label className="relative ml-3 inline-flex h-6 w-11 shrink-0 cursor-pointer items-center">
        <input
          id={name}
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={onChange}
        />
        <span className="block h-6 w-11 rounded-full bg-slate-200 transition-colors peer-checked:bg-slate-900" />
        <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
      </label>
    </div>
  );
}

const LabsPage: React.FC = () => {
  useEffect(() => { document.title = 'Labs'; }, []);
  const { settings, handleToggle } = useLabsSettings();

  return (
    <main className="space-y-4 px-4 py-6" id="main-content" role="main" aria-label="Labs">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Labs</h1>
        <p className="mt-1 text-sm text-slate-500">
          Ative recursos experimentais para testes e depuração.
        </p>
      </div>

      {/* Debug */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-3 px-5 pb-3 pt-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <Bug className="h-4 w-4" />
          </div>
          <h2 className="text-sm font-semibold text-slate-900">Debug</h2>
        </div>
        <div className="space-y-2 px-5 pb-5">
          <ToggleRow
            name="debug-logs"
            label="Debug logs"
            description="Exibe logs detalhados no console"
            checked={!!settings.debug}
            onChange={handleToggle('debug')}
          />
          <Separator />
          <ToggleRow
            name="debug-network"
            label="Debug network responses"
            description="Registra todas as respostas de rede"
            checked={!!settings.debugNetwork}
            onChange={handleToggle('debugNetwork')}
          />
          <Separator />
          <div className="pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => (window.location.href = '/debug-console')}
              disabled={!settings.debug}
              className="gap-2"
            >
              <Monitor className="h-4 w-4" aria-hidden="true" />
              Abrir console de debug
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            </Button>
            {!settings.debug && (
              <p className="mt-1.5 text-xs text-slate-400">
                Ative os logs de debug para acessar o console.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Work Status Page */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-3 px-5 pb-3 pt-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <Monitor className="h-4 w-4" />
          </div>
          <h2 className="text-sm font-semibold text-slate-900">Work Status Page</h2>
        </div>
        <div className="px-5 pb-5">
          <ToggleRow
            name="work-status-page"
            label="Ativar Work Status Page"
            checked={!!settings.workStatusPage}
            onChange={handleToggle('workStatusPage')}
          />
        </div>
      </div>

      {/* Motivational Feeds */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-3 px-5 pb-3 pt-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
            <Rss className="h-4 w-4" />
          </div>
          <h2 className="text-sm font-semibold text-slate-900">Motivational Feeds</h2>
        </div>
        <div className="px-5 pb-5">
          <ToggleRow
            name="motivational-feeds"
            label="Usar feeds motivacionais"
            checked={!!settings.useMotivationalFeeds}
            onChange={handleToggle('useMotivationalFeeds')}
          />
        </div>
      </div>

      {/* Debug info */}
      <div className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
          Debug Info
        </p>
        <pre className="max-h-40 overflow-auto rounded-lg bg-white p-3 font-mono text-[11px] text-slate-600">
          {JSON.stringify(settings, null, 2)}
        </pre>
      </div>
    </main>
  );
};

export default LabsPage;
